import { useCallback, useEffect, useRef, useState } from 'react';

import { useIsDocumentWorkflowTrackerInProgress } from '@/modules/workflowTrackers/hooks/useIsDocumentWorkflowTrackerInProgress';
import { WorkflowTrackerKind } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

/**
 * Manages the document summary generation and processing lifecycle.
 * Tracks summary generation state using both an immediate override (for instant UI feedback)
 * and household workflow tracker status (for actual processing state). Automatically refetches
 * document data when processing completes.
 */
export function useSummaryProcessing(
  documentId: string,
  householdId: string | undefined,
  onRefetch: () => void
) {
  const [isGeneratingOverride, setIsGeneratingOverride] = useState(false);
  const prevIsGeneratingRef = useRef(false);

  const isSummaryProcessing = useIsDocumentWorkflowTrackerInProgress(
    documentId,
    WorkflowTrackerKind.DocumentSummary
  );

  useEffect(() => {
    if (!isSummaryProcessing) {
      setIsGeneratingOverride(false);
    }
  }, [isSummaryProcessing]);

  const isGenerating = isSummaryProcessing || isGeneratingOverride;

  // Refetch only on transition from generating (true) to not generating (false)
  useEffect(() => {
    const prevIsGenerating = prevIsGeneratingRef.current;
    prevIsGeneratingRef.current = isGenerating;

    if (prevIsGenerating && !isGenerating) {
      diagnostics.debug(
        '[DocumentDetailsPage] Summary generation completed, refetching',
        { documentId, householdId }
      );
      onRefetch();
    }
  }, [isGenerating, onRefetch, documentId, householdId]);

  const setIsGeneratingAiSummaryOverride = useCallback(
    (isGeneratingAiSummaryOverride: boolean) => {
      setIsGeneratingOverride(isGeneratingAiSummaryOverride);
    },
    []
  );

  const onStartGenerating = useCallback(() => {
    setIsGeneratingAiSummaryOverride(true);
  }, [setIsGeneratingAiSummaryOverride]);

  return {
    isGenerating,
    onStartGenerating,
    setIsGeneratingAiSummaryOverride,
  };
}
